import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { observeAuthState, signOutUser } from '../services/authService';
import { getAllProperties, deleteProperty, updateProperty } from '../services/propertyService';
import { seedDatabase } from '../utils/seedDatabase';
import { Property } from '../types';
import Navbar from '../components/Navbar';

const AdminDashboardPage: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState<boolean>(false);
  const [seedMessage, setSeedMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const unsubscribe = observeAuthState((user, isAdmin) => {
      if (!user || !isAdmin) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load properties
  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const propertiesData = await getAllProperties();
      setProperties(propertiesData);
      setError(null);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      try {
        await deleteProperty(id);
        setProperties(properties.filter(property => property.id !== id));
        setError(null);
      } catch (err) {
        console.error("Error deleting property:", err);
        setError("Failed to delete property.");
      }
    }
  };

  const handleToggleAvailability = async (property: Property) => {
    try {
      const newAvailability = !property.available;
      await updateProperty(property.id, { available: newAvailability });
      
      setProperties(properties.map(p => 
        p.id === property.id 
          ? { ...p, available: newAvailability } 
          : p
      ));
      setError(null);
    } catch (err) {
      console.error("Error updating property availability:", err);
      setError("Failed to update property availability.");
    }
  };

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/admin/login');
  };

  // Handle database seeding
  const handleSeedDatabase = async () => {
    try {
      setSeeding(true);
      setSeedMessage(null);
      
      const result = await seedDatabase();
      
      if (result) {
        setSeedMessage({
          type: 'success',
          text: 'Successfully added sample properties to the database!'
        });
        // Refresh properties list to show newly added properties
        fetchProperties();
      } else {
        setSeedMessage({
          type: 'error',
          text: 'Sample properties already exist in the database.'
        });
      }
    } catch (err) {
      console.error("Error seeding database:", err);
      setSeedMessage({
        type: 'error',
        text: `Error seeding database: ${err instanceof Error ? err.message : String(err)}`
      });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container-custom mx-auto py-8">
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Property Management Dashboard</h1>
            <div className="flex space-x-2">
              <Link to="/admin/property/new" className="btn btn-primary">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Property
              </Link>
              <button onClick={handleSignOut} className="btn btn-outline">
                Sign Out
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
              <button 
                className="float-right font-bold"
                onClick={() => setError(null)}
              >
                &times;
              </button>
            </div>
          )}

          {/* Database Seeding Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-1">Database Management</h2>
                <p className="text-sm text-gray-600">Add sample properties to populate your database</p>
              </div>
              <button
                onClick={handleSeedDatabase}
                disabled={seeding}
                className="btn btn-secondary mt-3 sm:mt-0"
              >
                {seeding ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Seeding Database...
                  </>
                ) : "Seed Sample Properties"}
              </button>
            </div>
            
            {seedMessage && (
              <div className={`mt-3 p-3 rounded text-sm ${seedMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {seedMessage.text}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {properties.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No properties found.</p>
                  <Link to="/admin/property/new" className="btn btn-primary">
                    Add Your First Property
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Property
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {properties.map((property) => (
                        <tr key={property.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-16 w-20">
                                <img 
                                  className="h-16 w-20 object-cover rounded" 
                                  src={property.images[0] || 'https://via.placeholder.com/150'} 
                                  alt={property.title} 
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{property.title}</div>
                                <div className="text-sm text-gray-500">{property.location}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {property.bedrooms} beds • {property.bathrooms} baths • {property.area} sq ft
                            </div>
                            <div className="text-sm text-gray-500">{property.type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">${property.price.toLocaleString()}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span 
                              className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                property.available 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {property.available ? 'Available' : 'Not Available'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button 
                              onClick={() => handleToggleAvailability(property)}
                              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                property.available 
                                  ? 'text-red-700 bg-red-100 hover:bg-red-200' 
                                  : 'text-green-700 bg-green-100 hover:bg-green-200'
                              }`}
                            >
                              {property.available ? 'Mark Unavailable' : 'Mark Available'}
                            </button>
                            <Link 
                              to={`/admin/property/edit/${property.id}`} 
                              className="text-indigo-600 hover:text-indigo-900 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded bg-indigo-100"
                            >
                              Edit
                            </Link>
                            <button 
                              onClick={() => handleDelete(property.id)}
                              className="text-red-600 hover:text-red-900 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded bg-gray-100"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;